/**
 * What the Apps tab's in-page tabs share: a loader whose failure stays in its own card (`useLoaded`), the card with its
 * "Open …" link (`Card`), and the line under a tab's cards that opens the full tab (`OpenLink`).
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

export const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** `{ loading, value, error }` of `load()`, read again when `deps` change; a late answer for old deps is dropped. */
export function useLoaded(load, deps) {
  const [state, setState] = useState({ loading: true, value: null, error: null });
  useEffect(() => {
    let live = true;
    setState({ loading: true, value: null, error: null });
    Promise.resolve().then(load).then((value) => { if (live) { setState({ loading: false, value, error: null }); } })
      .catch((error) => { if (live) { setState({ loading: false, value: null, error: error || new Error('failed') }); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

export function Card({ title, link = null, onLink = null, testId, children, wide = false }) {
  return (
    <section className={`apps-info${wide ? ' is-wide' : ''}`} data-testid={testId}>
      <h3>{title}{link && onLink && <> <span aria-hidden="true">·</span> <button className="apps-info-link" onClick={onLink} type="button">{link}</button></>}</h3>
      {children}
    </section>
  );
}
Card.propTypes = {
  children: PropTypes.node, link: PropTypes.string, onLink: PropTypes.func, testId: PropTypes.string, title: PropTypes.node.isRequired, wide: PropTypes.bool,
};

/** "Open <tab>": the project's full tab on this service; nothing when the plan hides it or there is nowhere to go. */
export function OpenLink({ label, onOpen = null, testId = undefined }) {
  if (!onOpen) { return null; }
  return <p className="apps-open-row"><button className="apps-info-link" data-testid={testId} onClick={onOpen} type="button">{label} →</button></p>;
}
OpenLink.propTypes = { label: PropTypes.string.isRequired, onOpen: PropTypes.func, testId: PropTypes.string };

/** A card's body while loading or after a failure (the error's own words when it has some). */
export function LoadState({ state, empty = null }) {
  if (state.loading) { return <p className="apps-muted">Loading…</p>; }
  if (state.error) { return <p className="apps-muted">Not available{state.error?.message ? `: ${state.error.message}` : '.'}</p>; }
  return empty ? <p className="apps-muted">{empty}</p> : null;
}
LoadState.propTypes = { empty: PropTypes.string, state: PropTypes.object.isRequired };
